import {MenuCategoryItem} from "@components/Menu/interfaces";

const pagePaddingPx = 30;
const mainMenuSectionPaddingPx = 30;
const paddingBetweenItemsPx = 30;

export enum ColumnSizes {
  Small = "Small",
  Medium = "Medium",
  Big = "Big",
}

const getPaddingBetweenAllItems = (numberOfColumns: number): number =>
  (numberOfColumns - 1) * paddingBetweenItemsPx;

const getPadding = (numberOfColumns: number): number =>
  mainMenuSectionPaddingPx + getPaddingBetweenAllItems(numberOfColumns);

// Generates a 'calc' expression for the width of an image, for when the exact
// image width depends linearly on the viewport width (e.g. medium screen sizes)
export const getRelativeSize = (
  mainMenuSectionSize: number,
  numberOfColumns: number
): string => {
  // Express the width of the menu in vw units
  const menuWidth = `${(100 * mainMenuSectionSize).toFixed(2)}vw`;
  // Calculate the padding around the outside and between all dish images, and express in px
  const padding = `${getPadding(numberOfColumns)}px`;
  // Menu width minus padding gives total width of all images in a row. Divide by the number of columns
  // to get the width per image
  return `calc((${menuWidth} - ${padding}) / ${numberOfColumns})`;
};

// Calculates the maximum width of an image, for when a user's viewport width is
// wider than the breakpoint where the menu stops getting larger
const getFixedSize = (
  numberOfColumns: number,
  menuWidthMultiplier = 1
): string => {
  // Calculate the menu width: the content width, excluding the date selector
  const menuWidthPx = 1200 * menuWidthMultiplier;
  // Calculate the padding around the outside and between all dish images
  const paddingPx = getPadding(numberOfColumns);
  // Menu width minus padding gives total width of all images in a row. Divide by the number of columns
  // to get the width per image
  return `${(menuWidthPx - paddingPx) / numberOfColumns}px`;
};

export const getColumnType = (
  category: MenuCategoryItem,
  isTwoColumns?: boolean
): ColumnSizes => {
  if (isTwoColumns || category.column_number === 2) {
    return ColumnSizes.Big;
  }
  if (category.collapsible) return ColumnSizes.Small;
  return ColumnSizes.Medium;
};

// Calculates the "sizes" attribute to be used for dish images in a given category
export function getSizesAttribute({
  category,
  hasTwoColumns = false,
  hasDateSelector = false,
}: {
  category: MenuCategoryItem;
  hasTwoColumns?: boolean;
  hasDateSelector?: boolean;
}): string {
  // Calculates the maximum width of an image, for when a user's viewport width is
  // wider than the breakpoint where the menu stops getting larger

  // Below 640px, image always takes up full page width minus padding
  const smallSize = `calc(100vw - ${pagePaddingPx}px)`;

  // For anything larger, image size depends on number of columns

  const mediumMenuWidthMultiplier = hasDateSelector ? 9 / 12 : 1;
  const largeMenuWidthMultiplier = hasDateSelector ? 10 / 12 : 1;

  // Medium breakpoint: main menu section is medium-9 (i.e. 9/12 of the window width)
  let mediumSize = `(min-width: 640px) `;
  const columnClass: ColumnSizes = getColumnType(category, hasTwoColumns);
  if (columnClass === ColumnSizes.Medium) {
    mediumSize += getRelativeSize(mediumMenuWidthMultiplier, 3);
  } else {
    mediumSize += getRelativeSize(mediumMenuWidthMultiplier, 2);
  }

  // Large breakpoint: main menu section is large-10 (i.e. 10/12 of the window width)
  let largeSize = `(min-width: 1024px) `;
  if (columnClass === ColumnSizes.Small) {
    largeSize += getRelativeSize(largeMenuWidthMultiplier, 4);
  } else if (columnClass === ColumnSizes.Medium) {
    largeSize += getRelativeSize(largeMenuWidthMultiplier, 3);
  } else {
    largeSize += getRelativeSize(largeMenuWidthMultiplier, 2);
  }

  // Not an explicit breakpoint, but menu stops widening after 1200px so all images
  // stop getting any bigger after that point
  let maxSize = `(min-width: 1200px) `;
  if (columnClass === ColumnSizes.Big)
    maxSize += getFixedSize(2, largeMenuWidthMultiplier);
  else if (columnClass === ColumnSizes.Medium)
    maxSize += getFixedSize(3, largeMenuWidthMultiplier);
  else maxSize += getFixedSize(4, largeMenuWidthMultiplier);

  return [maxSize, largeSize, mediumSize, smallSize].join(`, `);
}
